import { CurrencyPipe, DatePipe } from "@angular/common";
import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { concatLatestFrom } from "@ngrx/operators";
import { Store } from "@ngrx/store";
import { of, throwError } from "rxjs";
import { catchError, exhaustMap, map, switchMap } from "rxjs/operators";
import { Classifications } from "../../account-info-header/models";
import { AccountInfoState } from "../../account-info-header/models/AccountInfoState";
import { selectClassifications, selectInfo, selectItStandingAuth } from "../../account-info-header/reducers";
import { EAuthInfo } from "../../shared/models/EAuthInfo";
import { TransAddTrustFields } from "../../shared/models/TransAddTrustFields";
import { PaidForDetails, TrustFieldsRequestInfo } from "../../shared/models/TrustFieldsRequestInfo";
import { RemoveHyphenFromAccountPipe } from "../../shared/pipes/remove-hyphen-from-account.pipe";
import { selectAcknowledgementDateTime, selectEAuthInfo } from "../../shared/reducers";
import { StatusViewService } from "../../shared/services/status-view.service";
import { StandingInstruction } from "../../shared/types/StandingInstruction";
import { TableColumn } from "../../shared/types/TableColumn";
import { TransactionsTableColumn } from "../../shared/types/TransactionTableColumn";
import { Constants, Frequencies, RegTypeCode, RequestTransactionType, ViewType, transactionCodes, yesNo } from "../../shared/utilities/Constants";
import { StandingAuthInternalTransferActions } from "../../standing-authorizations/actions";
import { ItStandingAuthDetails, StandingAuthorizationForm, isInternalTransferFormSA } from "../../standing-authorizations/models/standingAuthorizationForm";
import { selectInternalTransferEAuthModel, selectInternalTransferInfoIraFieldsSA, selectStandingAuthForm, selectStandingAuthInternalTransferInfo } from "../../standing-authorizations/reducers";
import { InternalTransferActions, TransactionFormActions } from "../actions";
import { PtrsInfo } from "../models";
import { ItExtraPrompts, ItTransactionDetails, TransactionForm, isInternalTransferForm } from "../models/form/TransactionForm";
import { ContributionReason } from "../models/internal-transfer/ContributionReason";
import { DistributionReason } from "../models/internal-transfer/DistributionReason";
import { EAuthModel } from "../models/internal-transfer/EAuthModel";
import { InternalTransferAcknowledgeRequest } from "../models/internal-transfer/InternalTransferAcknowledgeRequest";
import { InternalTransferAcknowledgeResponse } from "../models/internal-transfer/InternalTransferAcknowledgeResponse";
import { InternalTransferInfo } from "../models/internal-transfer/InternalTransferInfo";
import { InternalTransferInputResponse } from "../models/internal-transfer/InternalTransferInputResponse";
import { InternalTransferInquiryRequest } from "../models/internal-transfer/InternalTransferInquiryRequest";
import { InternalTransferInquiryResponse } from "../models/internal-transfer/InternalTransferInquiryResponse";
import { InternalTransferPositionsResponse } from "../models/internal-transfer/InternalTransferPositionsResponse";
import { InternalTransferVerifyRequest } from "../models/internal-transfer/InternalTransferVerifyRequest";
import { InternalTransferVerifyResponse } from "../models/internal-transfer/InternalTransferVerifyResponse";
import { IraFields } from "../models/internal-transfer/IraFields";
import { CodeAndDescription } from '../models/shared/CodeAndDescription';
import { ServiceError, ServiceErrorWithList } from "../models/shared/ServiceError";
import { selectCurrentTransaction, selectEditFLowFromTableComplete, selectEditStartedTable, selectInternalTransferInfo, selectPositionLotTableData, selectPtrsInfo, selectPtrsInfoPaidFor, selectStagedTransactionForm, selectTransactionForm } from "../reducers";
import { InternalTransferService } from "../services/internal-transfer.service";
import { InternalTransferInfoSA } from "../../standing-authorizations/models/InternalTransferInfoSA";
import { selectInitReleaseTransactionInContext } from "../../initiate-and-release/reducers";
import { DraftListAttributes } from "../../initiate-and-release/Models/Drafts/DraftResponse";
import { individual, PaidFor } from "../models/ptrs/PaidFor";
import { EAuthModelCustomer } from "../models/internal-transfer/EAuthModelCustomer";
import { ToastrService } from "ngx-toastr";

@Injectable()
export class InternalTransferEffects {
    hyphenPipe = new RemoveHyphenFromAccountPipe();

    constructor(
        private actions$: Actions,
        private internalTransferService: InternalTransferService,
        private statusViewService: StatusViewService,
        private store: Store,
        private currencyPipe: CurrencyPipe,
        private datepipe: DatePipe,
        private toastr: ToastrService
    ) { }

    loadToAccount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InternalTransferActions.toAccountEntered, InternalTransferActions.standingAuthSelected),
            switchMap((action) => {
                return this.internalTransferService.callInternalTransferInput(action.payload).pipe(
                    map((internalTransferInputResponse: InternalTransferInputResponse) => {
                        if (this.shouldReturnAccountError(internalTransferInputResponse)) {
                            return InternalTransferActions.loadToAccountFinishedWithError({ internalTransferInputResponse });
                        }
                        return InternalTransferActions.loadToAccountSuccess({ internalTransferInputResponse });
                    }),
                    catchError((err: ServiceError) =>
                        of(InternalTransferActions.loadToAccountFail(err))
                    )
                )
            })
        )
    );

    loadToAccountStandingAuthorization$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StandingAuthInternalTransferActions.toAccountEntered),
            switchMap((action) =>
                this.internalTransferService.callInternalTransferInput(action.payload).pipe(
                    map((internalTransferInputResponse: InternalTransferInputResponse) => {
                        if (this.shouldReturnAccountError(internalTransferInputResponse)) {
                            return StandingAuthInternalTransferActions.loadToAccountFinishedWithError({ internalTransferInputResponse });
                        }
                        return StandingAuthInternalTransferActions.loadToAccountSuccess({ internalTransferInputResponse });
                    }),
                    catchError((err: ServiceError) =>
                        of(StandingAuthInternalTransferActions.loadToAccountFail(err))
                    )
                )
            )
        )
    );

    callInquiry$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InternalTransferActions.callInquiry),
            concatLatestFrom(() => [
                this.store.select(selectInfo),
                this.store.select(selectEditStartedTable)
            ]),
            switchMap(([action, accountInfoState, isFromEditSubmitted]) => {
                let payload: InternalTransferInquiryRequest = {
                    cashieringRequestId: parseInt(action.payload.cashieringRqstId),
                    cashieringRqstSecondId: parseInt(action.payload.cashieringRqstSecondId),
                    isFromView: !isFromEditSubmitted,
                    subAccountId: accountInfoState.accountId,
                    masterAccountId: accountInfoState.masterAccount,
                    transactionType: this.getTransactionType(action.viewType, action.payload)
                }

                return this.statusViewService.callInternalInquiry(payload).pipe(
                    map((internalTransferInquiryResponse: InternalTransferInquiryResponse) => {
                        //TODO Need to map with appr. model
                        return InternalTransferActions.inquiryCallSuccessful({ internalTransferInquiryResponse });
                    }),
                    catchError((err: ServiceError) => of(InternalTransferActions.inquiryCallFail(err))
                    )
                );
            }
            )
        )
    );

    callPositions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InternalTransferActions.loadPositions),
            concatLatestFrom(() => [
                this.store.select(selectInfo),
                this.store.select(selectInternalTransferInfo),
                this.store.select(selectTransactionForm),
            ]),
            exhaustMap(([action, accountInfo, itInfo, transactionForm]) => {
                return this.internalTransferService.callInternalTransferPositions(accountInfo.accountId, this.hyphenPipe.transform(itInfo.toAccountInfo.accountNumber)).pipe(
                    map((internalTransferPositionsResponse: InternalTransferPositionsResponse) => {
                        const positions = internalTransferPositionsResponse?.positions;
                        if (positions !== null && positions !== undefined) {
                            positions.map(position => {
                                if (position?.costBasis !== null && position?.costBasis !== undefined) {
                                    position.costBasis = this.currencyPipe.transform(position?.costBasis)
                                }
                                if (position?.currentPricePerShare !== null && position?.currentPricePerShare !== undefined) {
                                    position.currentPricePerShare = this.currencyPipe.transform(position?.currentPricePerShare)
                                }
                                return position;
                            });
                        }
                        return InternalTransferActions.loadPositionsSuccessful({ internalTransferPositionsResponse });
                    }),
                    catchError((err: ServiceError) =>
                        of(InternalTransferActions.loadPositionsFail(err))
                    )
                )
            })
        )
    )

    callVerify$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TransactionFormActions.callInternalTransferVerify),
            concatLatestFrom(() => [
                this.store.select(selectTransactionForm),
                this.store.select(selectInternalTransferInfo),
                this.store.select(selectInfo),
                this.store.select(selectItStandingAuth),
                this.store.select(selectPositionLotTableData),
                this.store.select(selectCurrentTransaction),
                this.store.select(selectInitReleaseTransactionInContext),
                this.store.select(selectPtrsInfoPaidFor),
            ]),
            exhaustMap(([action, transactionForm, itInfo, accountInfo, instructions, positionsInfo, currentTransaction, initReleaseTransactionInContext, paidForInfo]) => {
                if (isInternalTransferForm(transactionForm)) {
                    let request = this.createVerifyRequest(
                        transactionForm,
                        itInfo,
                        accountInfo,
                        transactionForm.extraPrompts,
                        instructions,
                        positionsInfo,
                        currentTransaction!,
                        initReleaseTransactionInContext,
                        action.isInRViewReviewFlow,
                        paidForInfo,
                    );
                    return this.internalTransferService.callInternalTransferVerify(request).pipe(
                        map((response: InternalTransferVerifyResponse) => {
                            if (response.hasError) {
                                return InternalTransferActions.verifyCallFinishedWithError({ response });
                            }
                            return InternalTransferActions.verifyCallSuccessful({ response });
                        }),
                        catchError((err: ServiceError) =>
                            of(InternalTransferActions.verifyCallFail(err))
                        )
                    )
                }
                return throwError(() => new Error('invalid form type for internal transfer verify'));
            })
        )
    );

    callVerifyStandingAuthorizationCustomRulesSplitJournal$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TransactionFormActions.callInternalTransferVerifyCustomRule),
            concatLatestFrom(() => [
                this.store.select(selectTransactionForm),
                this.store.select(selectInfo),
                this.store.select(selectInitReleaseTransactionInContext),
                this.store.select(selectInternalTransferInfo),
            ]),
            exhaustMap(([_action, form, info, inrDraftInContext, internalTransferInfo]) => {
                let request = this.createVerifyRequestStandingAuthorizationCustomRulesSplit(form, info, inrDraftInContext, internalTransferInfo);
                return this.internalTransferService.callInternalTransferVerify(request).pipe(
                    map((response) => {
                        if (response.hasError) {
                            this.toastr.error('Standing authorization creation currently unavailable.');
                            return StandingAuthInternalTransferActions.verifyCallFinishedWithError({ response });
                        }
                        return StandingAuthInternalTransferActions.verifyCallSuccessful({ response });
                    }),
                    catchError((err: ServiceError) => {
                        this.toastr.error('Standing authorization creation currently unavailable.');
                        return of(StandingAuthInternalTransferActions.verifyCallFail(err));
                    })
                )
            })
        )
    );

    callVerifyStandingAuthorization$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StandingAuthInternalTransferActions.callInternalTransferVerify),
            concatLatestFrom(() => [
                this.store.select(selectStandingAuthForm),
                this.store.select(selectInternalTransferInfoIraFieldsSA),
                this.store.select(selectInfo),
                this.store.select(selectInitReleaseTransactionInContext),
            ]),
            exhaustMap(([_action, form, iraFields, info, inrDraftInContext]) => {
                if (isInternalTransferFormSA(form)) {
                    let request = this.createVerifyRequestStandingAuthorization(form, iraFields, info, inrDraftInContext);
                    return this.internalTransferService.callInternalTransferVerify(request).pipe(
                        map((response) => {
                            if (response.hasError) {
                                return StandingAuthInternalTransferActions.verifyCallFinishedWithError({ response });
                            }
                            return StandingAuthInternalTransferActions.verifyCallSuccessful({ response });
                        }),
                        catchError((err: ServiceError) => of(StandingAuthInternalTransferActions.verifyCallFail(err)))
                    )
                }
                return throwError(() => new Error('invalid form type for internal transfer verify'));
            })
        )
    )


    callAcknowledge$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InternalTransferActions.callAcknowledge),
            concatLatestFrom(() => [
                this.store.select(selectInfo),
                this.store.select(selectInternalTransferInfo),
                this.store.select(selectStagedTransactionForm),
                this.store.select(selectItStandingAuth),
                this.store.select(selectClassifications),
                this.store.select(selectPtrsInfo),
                this.store.select(selectAcknowledgementDateTime),
                this.store.select(selectPositionLotTableData),
                this.store.select(selectEAuthInfo),
                this.store.select(selectEditFLowFromTableComplete),
                this.store.select(selectCurrentTransaction),
                this.store.select(selectPtrsInfoPaidFor),
                this.store.select(selectInitReleaseTransactionInContext)
            ]),
            exhaustMap(([
                _action,
                accountInfo,
                itInfo,
                transactionFormState,
                standingInstructions,
                classifications,
                ptrsInfo,
                acknowledgementConsentDateTime,
                positionsInfo,
                eauthInfo,
                isEdit,
                currentTransaction,
                paidForInfo,
                initReleaseTransactionInContext
            ]) => {

                let request = this.createAcknowledgeRequest(
                    accountInfo,
                    itInfo,
                    transactionFormState.transactionDetails as ItTransactionDetails,
                    transactionFormState.extraPrompts as ItExtraPrompts,
                    standingInstructions,
                    eauthInfo,
                    classifications,
                    ptrsInfo,
                    acknowledgementConsentDateTime,
                    positionsInfo,
                    isEdit,
                    currentTransaction!,
                    paidForInfo,
                    initReleaseTransactionInContext
                );
                return this.internalTransferService.callInternalTransferAcknowledge(request).pipe(
                    map((response: InternalTransferAcknowledgeResponse) => {
                        return InternalTransferActions.acknowledgeCallSuccessful({ response });
                    }),
                    catchError((err: ServiceErrorWithList) =>
                        of(InternalTransferActions.acknowledgeCallFail(err))
                    )
                )
            })
        )
    );

    callAcknowledgeStandingAuthorization$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StandingAuthInternalTransferActions.callAcknowledge),
            concatLatestFrom(() => [
                this.store.select(selectInternalTransferEAuthModel),
                this.store.select(selectEAuthInfo),
                this.store.select(selectInfo),
                this.store.select(selectStandingAuthForm),
                this.store.select(selectStandingAuthInternalTransferInfo),
                this.store.select(selectAcknowledgementDateTime)
            ]),
            switchMap(([_action, eauthModel, eAuthInfo, accountInfo, form, itInfo, acknowledgementConsentDateTime]) => {
                let request =
                    this.createAcknowledgeRequestStandingAuthorization(
                        eauthModel,
                        eAuthInfo,
                        accountInfo,
                        form.standingAuthorizationDetails as ItStandingAuthDetails,
                        itInfo,
                        acknowledgementConsentDateTime,
                        form.isSubmitAndCreateBundledSa ?? false
                    );
                return this.internalTransferService.callInternalTransferAcknowledge(request).pipe(
                    map((response: InternalTransferAcknowledgeResponse) => {
                        return StandingAuthInternalTransferActions.acknowledgeCallSuccessful({ response });
                    }),
                    catchError((err: ServiceErrorWithList) => of(StandingAuthInternalTransferActions.acknowledgeCallFail(err)))
                )
            })
        )
    );

    private shouldReturnAccountError(
        internalTransferInputResponse: InternalTransferInputResponse
    ): boolean {
        const regType: string = internalTransferInputResponse?.toAccountInfo?.registrationType ?? '';
        const is401kToAccount: boolean = regType === RegTypeCode.ROTH_401K || regType === RegTypeCode.INDIVIDUAL_401K;
        const errorText = internalTransferInputResponse.errorText ?? '';

        const hasInvalidAccountNumber = regType === '' || errorText?.toUpperCase() === Constants.toAccountEightDigitMsg.toUpperCase() || errorText?.toUpperCase() === Constants.toAccountWrongDigitsMsg.toUpperCase(); // checking for either error message to decouple change from change to journals-api  

        const has401kRestriction = is401kToAccount && (
            errorText.trim().length > 0 && errorText?.trim().toUpperCase() !== Constants.recipentError.toUpperCase()
        );

        return hasInvalidAccountNumber || has401kRestriction;
    }

    createVerifyRequest(transactionForm: TransactionForm, itInfo: InternalTransferInfo, accountInfo: AccountInfoState, extraPrompts: ItExtraPrompts, instructions: StandingInstruction[], positionsInfo: TableColumn[], currentTransaction: TransactionsTableColumn, InRTransactionInContext: DraftListAttributes, isInRViewReviewFlow: boolean, paidForInfo: PaidFor): InternalTransferVerifyRequest {
        const isEdit = transactionForm.prefilledFlow.name === 'edit-table' && transactionForm.prefilledFlow.state === 'complete';
        const transactionDetails = transactionForm.transactionDetails as ItTransactionDetails;
        const isBsloaTrans = extraPrompts.useStandingAuth === 'bundled';
        const frequency = this.getFrequencyType(extraPrompts, isBsloaTrans);

        const useSloa = isBsloaTrans ? extraPrompts.useBundledStandingAuth === 'yes' : extraPrompts.useStandingAuth === yesNo.yes;

        const isRecurring: boolean = frequency === Frequencies.recurring;
        const blankIraContributionType: ContributionReason = {
            code: '',
            description: '',
            subCode: '',
            year: ''
        }
        const standingInstruction = instructions.find(instruction => extraPrompts.standingAuthId == 'i-' + instruction.cashieringRqstId + '-' + instruction.cashieringRqstSecondId);
        const stateTaxPercent: string = itInfo.iraFields.isIraTransaction ? transactionDetails.taxWithholdingsFormGroup.stateTaxPercent.replace('%', '') : '0';
        const fedTaxPercent: string = itInfo.iraFields.isIraTransaction ? transactionDetails.taxWithholdingsFormGroup.fedTaxPercent.replace('%', '') : '0';
        // Todo - Add cost basis method and selectedLotCount for InR flow
        const selectedCostBasisMethod: string = !isInRViewReviewFlow ? this.getSelectedCostBasisMethod(positionsInfo, itInfo?.positionsLotDetails?.costMethod) : InRTransactionInContext?.draftObject?.selectedCostBasisMethod ?? '';
        const selectedLotCount = selectedCostBasisMethod === 'None' ? !isInRViewReviewFlow ? this.getSelectedlotCount(positionsInfo) : InRTransactionInContext?.draftObject?.selectedLotsCount ?? 0 : 0;
        let cashieringRequestId: number = isEdit
            ? Number.parseInt(currentTransaction.CashieringRqstId!)
            : standingInstruction?.cashieringRqstId
                ? Number.parseInt(standingInstruction!.cashieringRqstId)
                : 0;
        let cashieringRequestSecondId: number = isEdit
            ? Number.parseInt(currentTransaction.CashieringRqstSecondId!)
            : standingInstruction?.cashieringRqstSecondId
                ? Number.parseInt(standingInstruction!.cashieringRqstSecondId)
                : 0;

        if (isInRViewReviewFlow) {
            cashieringRequestId = +(InRTransactionInContext.draftObject.cashieringRequestId ?? 0);
            cashieringRequestSecondId = +(InRTransactionInContext.draftObject.cashieringSecondRequestId ?? 0);
        }
        let paidForContact: individual = (isInRViewReviewFlow ? InRTransactionInContext.draftObject.paidFor : paidForInfo.individuals.find(individual => (transactionDetails?.trustPaidFor?.toLocaleLowerCase().includes(individual.name.toLocaleLowerCase())) && (transactionDetails.trustPaidFor?.toLocaleLowerCase().includes(individual.role.toLocaleLowerCase()))) ?? new PaidForDetails()) ?? new PaidForDetails();
        return {
            draftId: InRTransactionInContext.draftId,
            bsloaTransaction: isBsloaTrans,
            stateTaxAttentionRequired: false,
            amount: (extraPrompts.transferType !== 'positions') ? transactionDetails.amount.replace('$', '') : '0',
            amountType: (extraPrompts.transferType === 'positions') ? '' : transactionDetails.amountType,
            processDate: this.datepipe.transform(transactionDetails.processDate, 'yyyy-MM-dd') ?? '',
            isIraTransaction: itInfo.inputIraFields.isIraTransaction,
            isWithholdFedTax: Number.parseFloat(fedTaxPercent) !== 0,
            fedTaxAmount: fedTaxPercent, //the verify call wants percent to be sent as amount
            isWithholdStateTax: Number.parseFloat(stateTaxPercent) !== 0,
            stateTaxAmount: stateTaxPercent,
            isSloaPopupSelected: useSloa,
            isCreateSloa: InRTransactionInContext.draftObject.isCreateSloa ?? false,
            nickname: '',
            isRecurringTransaction: isRecurring,
            process2ndDate: isRecurring ? transactionDetails.recurringFieldsFormGroup.secondProcessDate : '',
            endDate: isRecurring ? transactionDetails.recurringFieldsFormGroup.endDate : '',
            isTrust: accountInfo.classifications.isPtrs,
            paidFor: paidForContact,
            trustIncmPct: transactionDetails.trustIncomePercent ?? '',
            trustIncmToPct: transactionDetails.trustIncomeToPercent ?? '',
            trustPrinPct: transactionDetails.trustPrincipalPercent ?? '',
            trustPrinToPct: transactionDetails.trustPrincipalToPercent ?? '',
            trustDepositCode: '',
            trustTaxCode: transactionDetails.trustTaxCode ?? '',
            trustTaxCategory: transactionDetails.trustTaxCategory ?? '',
            depositTaxCode: transactionDetails.trustDepositCode ?? '',
            toIncome: (parseFloat(transactionDetails.trustIncomeToPercent!)).toString(),
            toPrincipal: (parseFloat(transactionDetails.trustPrincipalToPercent!)).toString(),
            defaultCostBasisMethod: !isInRViewReviewFlow ? itInfo?.positionsLotDetails?.costMethod : InRTransactionInContext?.draftObject?.defaultCostBasisMethod ?? '',
            selectedCostBasisMethod: selectedCostBasisMethod,
            selectedPositionsCount: !isInRViewReviewFlow ? this.getSelectedPositionCount(positionsInfo) : InRTransactionInContext.draftObject.selectedPositionsCount ?? 0,
            // Todo - Add lots detail for InR transaction
            selectedLotsCount: selectedLotCount,
            selectedPositionsList: !isInRViewReviewFlow ? this.createSelectedPositionsList(positionsInfo) : InRTransactionInContext.draftObject.selectedPositionsList ?? [],
            isLoaOnFile: false,
            isEdit: isEdit,
            cashieringNoteText: '',
            stockHandlerId: itInfo?.positionsInfo?.cmtRequestId !== null ? itInfo?.positionsInfo?.cmtRequestId : 0,
            subAccountId: accountInfo.accountId,
            masterAccountId: accountInfo.masterAccount,
            toAccountId: this.hyphenPipe.transform(transactionDetails.toAccount),
            tranMemo: transactionDetails.trustComment ?? '',
            distributionReason: itInfo.inputIraFields.isIraTransaction ? itInfo.inputIraFields.distributionReasonMap.filter(elem => elem.description === transactionDetails.taxWithholdingsFormGroup.distributionReason)[0] : new DistributionReason(),
            payIncome: isRecurring && transactionDetails.recurringFieldsFormGroup.payIncome ? itInfo.recurringFields.payIncomeMap.filter(elem => elem.code === transactionDetails.recurringFieldsFormGroup.payIncome)[0] : new CodeAndDescription(),
            frequency: isRecurring ? itInfo.recurringFields.frequencyList.filter(elem => elem.code === transactionDetails.recurringFieldsFormGroup.recurringFrequency)[0] : new CodeAndDescription(),
            iraContributionType: itInfo.iraContributionTypeMap.length > 0 ? itInfo.iraContributionTypeMap.filter(elem => elem.description === transactionDetails.iraContributionType)[0] : blankIraContributionType,
            cashieringRequestId: cashieringRequestId,
            cashieringSecondRequestId: cashieringRequestSecondId,
            stateCd: itInfo.stateCd
        }
    }

    createVerifyRequestStandingAuthorization(form: StandingAuthorizationForm, iraFields: IraFields, accountInfo: AccountInfoState, InRTransactionInContext: DraftListAttributes): InternalTransferVerifyRequest {
        let standingAuthDetails: ItStandingAuthDetails = form.standingAuthorizationDetails as ItStandingAuthDetails;
        return new InternalTransferVerifyRequest({
            draftId: InRTransactionInContext?.draftId ?? '',
            isWithholdFedTax: iraFields.isWithholdFedTax,
            isWithholdStateTax: iraFields.isWithholdStateTax,
            isCreateSloa: true,
            subAccountId: accountInfo.accountId,
            masterAccountId: accountInfo.masterAccount,
            toAccountId: this.hyphenPipe.transform(standingAuthDetails.toAccount),
            isIraTransaction: iraFields.isIraTransaction,
            fedTaxAmount: accountInfo.classifications.isIra ? standingAuthDetails.fedTaxPercent.replace('%', '') : '0',
            stateTaxAmount: accountInfo.classifications.isIra ? standingAuthDetails.stateTaxPercent.replace('%', '') : '0',
            nickname: standingAuthDetails.authorizationNickname ?? '',
            bsloaTransaction: form.isSubmitAndCreateBundledSa,
            processDate: new Date().toLocaleString(),
            iraContributionType: {
                code: "",
                year: "",
                subCode: "",
                description: ""
            }
        })
    }

     createVerifyRequestStandingAuthorizationCustomRulesSplit(transactionForm: TransactionForm, accountInfo: AccountInfoState, InRTransactionInContext: DraftListAttributes,internalTransferInfo: InternalTransferInfo): InternalTransferVerifyRequest {
        const transactionDetails = transactionForm.transactionDetails as ItTransactionDetails;
        const stateTaxPercent: string = accountInfo.classifications.isIra ? internalTransferInfo.iraFields.stateTaxPercent.replace('%', '') : '0';
        const fedTaxPercent: string = accountInfo.classifications.isIra ? internalTransferInfo.iraFields.fedTaxPercent.replace('%', '') : '0';
        return new InternalTransferVerifyRequest({
            draftId: InRTransactionInContext?.draftId ?? '',
            isWithholdFedTax: internalTransferInfo.iraFields.isWithholdFedTax,  
            isWithholdStateTax: internalTransferInfo.iraFields.isWithholdStateTax,
            isCreateSloa: true,
            subAccountId: accountInfo.accountId,
            masterAccountId: accountInfo.masterAccount,
            toAccountId: this.hyphenPipe.transform(transactionDetails.toAccount),
            isIraTransaction: internalTransferInfo.iraFields.isIraTransaction,
            fedTaxAmount: fedTaxPercent,
            stateTaxAmount: stateTaxPercent,
            nickname: '',
            bsloaTransaction: false,
            processDate: new Date().toLocaleString(),
            iraContributionType: {
                code: "",
                year: "",
                subCode: "",
                description: ""
            }
        })
    }

    getSelectedPositionCount(positions: TableColumn) {
        let positionsChanged = 0;
        for (var i = 0; i < positions.length; i++) {
            if (positions[i]['Shares to move'] !== '') {
                positionsChanged++;
            }
        }
        return positionsChanged;
    }

    getFrequencyType(extraPrompts: ItExtraPrompts, isBsloaTrans: boolean) {
        if (isBsloaTrans) {
            if (extraPrompts.useBundledStandingAuth === 'no') {
                return Frequencies.onetime;
            } else if (extraPrompts.useBundledStandingAuth === 'recurring' || (extraPrompts.useBundledStandingAuth === 'yes' && extraPrompts.frequency === 'recurring')) {
                return Frequencies.recurring;
            }
            return '';
        }
        return extraPrompts.frequency;
    }

    getSelectedCostBasisMethod(positions: TableColumn, defaultCostMethod: string): string {
        if (positions.length > 0) {
            return positions[0].nodeCostBasis;
        }
        return defaultCostMethod;
    }

    getSelectedlotCount(positions: TableColumn) {
        let lotCount = 0;
        positions.forEach(function (position: TableColumn) {
            position['lotDetail'].forEach(function (lot: TableColumn) {
                if (lot['lotsToMove'] !== '' && lot['lotsToMove'] !== undefined) {
                    lotCount++;
                }
            });
        });
        return lotCount;
    }

    getLotDetails(position: TableColumn, currentPricePerShare: number) {
        let lots: Array<any> = [];
        lots = position.map((lot: TableColumn) => {
            if (lot['lotsToMove'] !== '' && lot['lotsToMove'] !== undefined) {
                return {
                    lotId: lot['lotId'],
                    dateAcquired: lot['acquiredDate'],
                    holdingPeriodCode: this.getHoldingPeriodCode(lot['holdingPeriod']),
                    selectedQuantity: lot['lotsToMove'],
                    purchasePrice: lot['purchasePrice'],
                    purchaseDate: lot['purchaseDate'],
                    quantity: lot['lotQuantity'],
                    costBasis: lot['costBasis'],
                    marketValue: Number(lot["lotsToMove"]) * currentPricePerShare,
                }
            }
        });
        lots = lots.filter(Boolean);
        return lots;
    }

    getHoldingPeriodCode(holdingPeriod: string): string {
        switch (holdingPeriod) {
            case 'Short':
                return 'S';
            case 'Long':
                return 'L';
            default:
                return '';
        }
    }

    createSelectedPositionsList(positions: TableColumn) {
        let lotDetail: Array<any> = [];
        lotDetail = positions.map((position: any) => {
            let quantity = parseFloat(position["Available shares"].split(' ')[0].replace(/[\$,]/g, ''));
            let price = parseFloat((position["Current price/share"]).toString().replace(/[\$,]/g, ''));
            let selectedMarketValue = (price * position['Shares to move'] * Number(position['valMultiplier'])).toFixed(2);
            if (position['Shares to move'] !== '') {
                return {
                    "selectedQuantity": position['Shares to move'],
                    "schwabSecurityId": position['schwabSecurityId'],
                    "symbol": position['Symbol'],
                    "fullName": position['Name'],
                    "cmtSequenceId": position['cmtSequenceId'],
                    "multiplier": position['valMultiplier'],
                    "costBasis": position['Cost basis'],
                    "lots": this.getLotDetails(position['lotDetail'], price),
                    "quantity": quantity,
                    "price": price,
                    "selectedMarketValue": selectedMarketValue,
                    "numberOfLots": position['lotDetail'].length
                }
            }
        })
        lotDetail = lotDetail.filter(Boolean);
        return lotDetail;
    }

    createAcknowledgeRequest(
        accountInfo: AccountInfoState,
        itInfo: InternalTransferInfo,
        transactionDetails: ItTransactionDetails,
        extraPrompts: ItExtraPrompts,
        instructions: StandingInstruction[],
        eAuthInfo: EAuthInfo,
        classifications: Classifications,
        ptrsInfo: PtrsInfo,
        acknowledgementConsentDateTime: string,
        positionsInfo: TableColumn[],
        isEdit: boolean,
        currentTransaction: TransactionsTableColumn,
        paidForInfo: PaidFor,
        initReleaseTransactionInContext: DraftListAttributes
    ): InternalTransferAcknowledgeRequest {
        const blankIraContributionType: ContributionReason = {
            code: '',
            description: '',
            subCode: '',
            year: ''
        }

        const standingInstruction = instructions.find(instruction => extraPrompts.standingAuthId == 'i-' + instruction.cashieringRqstId + '-' + instruction.cashieringRqstSecondId);
        const stateTaxPercent: string = itInfo.iraFields.isIraTransaction ? transactionDetails.taxWithholdingsFormGroup.stateTaxPercent.replace('%', '') : '0';
        const fedTaxPercent: string = itInfo.iraFields.isIraTransaction ? transactionDetails.taxWithholdingsFormGroup.fedTaxPercent.replace('%', '') : '0';
        const selectedCostBasisMethod: string = this.getSelectedCostBasisMethod(positionsInfo, itInfo?.positionsLotDetails?.costMethod);
        const isLivingTrustFlow = accountInfo?.registrationType?.toLowerCase() === "liv trust";

        const cashieringRequestId: number = isEdit
            ? Number.parseInt(currentTransaction.CashieringRqstId!)
            : standingInstruction?.cashieringRqstId
                ? Number.parseInt(standingInstruction!.cashieringRqstId)
                : itInfo.cashieringReqId
                    ? itInfo.cashieringReqId
                    : 0;
        const cashieringRequestSecondId: number = isEdit
            ? Number.parseInt(currentTransaction.CashieringRqstSecondId!)
            : standingInstruction?.cashieringRqstSecondId
                ? Number.parseInt(standingInstruction!.cashieringRqstSecondId)
                : itInfo.cashieringReqSecondaryId
                    ? itInfo.cashieringReqSecondaryId
                    : 0;
        let selectedEauthCustomer = eAuthInfo.isAccountHolderSelected ? itInfo.eAuthModel.custAcctAuthorizationsList.eAuthCustomerList.filter(customer => customer.customerId === eAuthInfo.selectedAccountHolder[0]) : [];
        let paidForContact: individual = initReleaseTransactionInContext.draftObject.paidFor ? initReleaseTransactionInContext.draftObject.paidFor : paidForInfo.individuals.find(individual => (transactionDetails.trustPaidFor?.toLocaleLowerCase().includes(individual.name.toLocaleLowerCase())) && (transactionDetails.trustPaidFor?.toLocaleLowerCase().includes(individual.role.toLocaleLowerCase()))) ?? new PaidForDetails();
        return {
            subAccountId: this.hyphenPipe.transform(itInfo.accountInfoBanner.subAccountNumber),
            masterAccountId: this.hyphenPipe.transform(itInfo.accountInfoBanner.masterAccount),
            toAccountId: this.hyphenPipe.transform(itInfo.toAccountInfo.accountNumber),
            bsloaTransaction: extraPrompts.useStandingAuth === 'bundled',
            stateTaxAttentionRequired: itInfo.inputIraFields.stateTaxAttentionRequired,
            amount: (extraPrompts.transferType !== 'positions') ? transactionDetails.amount.replace('$', '') : '0',
            amountType: (extraPrompts.transferType === 'positions') ? '' : transactionDetails.amountType,
            processDate: itInfo.processDate,
            isIraTransaction: itInfo.inputIraFields.isIraTransaction,
            isWithholdFedTax: Number.parseFloat(fedTaxPercent) !== 0,
            fedTaxAmount: fedTaxPercent, //the ack call also wants percent to be sent as amount
            isWithholdStateTax: Number.parseFloat(stateTaxPercent) !== 0,
            stateTaxAmount: stateTaxPercent,
            isSloaPopupSelected: this.isSloaSelected(extraPrompts),
            isCreateSloa: false,
            nickname: '',
            cashieringRequestId: cashieringRequestId,
            cashieringSecondRequestId: cashieringRequestSecondId,
            isRecurringTransaction: itInfo.recurringFields.isRecurringTransaction,
            process2ndDate: itInfo.recurringFields.secondProcessDate,
            endDate: itInfo.recurringFields.endDate,
            paidFor: paidForContact,
            trustIncmPct: itInfo.trustFields.trustIncmPct,
            trustIncmToPct: transactionDetails.trustIncomeToPercent ?? '',
            trustPrinPct: itInfo.trustFields.trustPrinPct,
            trustPrinToPct: transactionDetails.trustPrincipalToPercent ?? '',
            trustTaxCode: itInfo.trustFields.trustTaxCode,
            trustTaxCategory: transactionDetails.trustTaxCategory ?? '',
            trustDepositCode: itInfo.trustFields.trustDepositCode,
            depositTaxCode: transactionDetails.trustDepositCode ?? '',
            toIncome: itInfo.toAccountInfo.toIncome ?? '',
            toPrincipal: itInfo.toAccountInfo.toPrincipal ?? '',
            defaultCostBasisMethod: itInfo?.positionsLotDetails?.costMethod,
            selectedCostBasisMethod: selectedCostBasisMethod,
            selectedPositionsCount: this.getSelectedPositionCount(positionsInfo),
            selectedLotsCount: selectedCostBasisMethod === 'None' ? this.getSelectedlotCount(positionsInfo) : 0,
            selectedPositionsList: this.createSelectedPositionsList(positionsInfo),
            isLoaOnFile: false,
            isEdit: isEdit,
            eauthSelected: eAuthInfo.isAccountHolderSelected,
            isSubmitAndCreateSLOA: itInfo.isSubmitAndCreate,
            customerList: selectedEauthCustomer,
            customerListSloa: extraPrompts.useStandingAuth === 'yes' ? [] : mapCustomerListSloa(itInfo.isSubmitAndCreate, selectedEauthCustomer, itInfo.eAuthModel),
            stockHandlerId: itInfo?.positionsInfo?.cmtRequestId !== null ? itInfo?.positionsInfo?.cmtRequestId : 0,
            isUseSloa: this.isSloaSelected(extraPrompts),
            cashieringNoteText: '',
            tranMemo: '',
            distributionReason: itInfo.inputIraFields.isIraTransaction ? itInfo.inputIraFields.distributionReasonMap.filter(elem => elem.description === transactionDetails.taxWithholdingsFormGroup.distributionReason)[0] : new DistributionReason(),
            payIncome: itInfo.recurringFields.payIncome,
            frequency: itInfo.recurringFields.frequency,
            iraContributionType: itInfo.iraContributionTypeMap.length > 0 ? itInfo.iraContributionTypeMap.filter(elem => elem.description === transactionDetails.iraContributionType)[0] : blankIraContributionType,
            acknowledgementConsentDateTime: acknowledgementConsentDateTime,
            trustFieldsRequestInfo: this.mapAddTransFields(classifications, transactionDetails, ptrsInfo, itInfo, paidForContact),
            draftId: itInfo.journalAuthResponse?.draftId,
            isThirdParty: itInfo.isThirdParty ?? false,
            stateCd: itInfo.stateCd
        };
    }

    createAcknowledgeRequestStandingAuthorization(
        eAuthModel: EAuthModel,
        eAuthInfo: EAuthInfo,
        accountInfo: AccountInfoState,
        standingAuthDetails: ItStandingAuthDetails,
        itInfo: InternalTransferInfoSA,
        acknowledgementConsentDateTime: string,
        isSubmitAndCreateBundledSa: boolean
    ): InternalTransferAcknowledgeRequest {
        let selectedEauthCustomers = eAuthInfo.selectedAccountHolder
            .map(customerId => {
                return eAuthModel.custAcctAuthorizationsList.eAuthCustomerList.find(customer => customer.customerId === customerId);
            })
            .filter(customer => customer !== undefined) as EAuthModelCustomer[];
        eAuthInfo.isAccountHolderSelected ? itInfo.eAuthModel.custAcctAuthorizationsList.eAuthCustomerList.filter(customer => customer.customerId === eAuthInfo.selectedAccountHolder[0]) : [];
        let response = new InternalTransferAcknowledgeRequest({
            customerListSloa: selectedEauthCustomers,
            eauthSelected: eAuthInfo.isAccountHolderSelected,
            isCreateSloa: true,
            subAccountId: accountInfo.accountId,
            masterAccountId: accountInfo.masterAccount,
            toAccountId: this.hyphenPipe.transform(standingAuthDetails.toAccount),
            nickname: standingAuthDetails.authorizationNickname,
            acknowledgementConsentDateTime: acknowledgementConsentDateTime,
            bsloaTransaction: isSubmitAndCreateBundledSa,
            draftId: itInfo.journalAuthResponse?.draftId,
            customerList: selectedEauthCustomers,
            isThirdParty: itInfo.isThirdParty ?? false
        })
        if (itInfo.iraFields.isIraTransaction) {
            const stateTaxPercent: string = standingAuthDetails.stateTaxPercent.replace('%', '');
            const fedTaxPercent: string = standingAuthDetails.fedTaxPercent.replace('%', '');

            response.fedTaxAmount = fedTaxPercent;
            response.stateTaxAmount = stateTaxPercent;
            response.isWithholdFedTax = Number.parseFloat(fedTaxPercent) !== 0;
            response.isWithholdStateTax = Number.parseFloat(stateTaxPercent) !== 0;
        }
        return response;
    }

    mapAddTransFields(classifications: Classifications, transactionDetails: ItTransactionDetails, ptrsInfo: PtrsInfo, itInfo: InternalTransferInfo, selectedContact: PaidForDetails): TransAddTrustFields | null {
        let fromTrustFields: TrustFieldsRequestInfo | null = null;
        let toTrustFields: TrustFieldsRequestInfo | null = null;

        if (classifications.isPtrs) {
            fromTrustFields = {
                accountNumber: this.hyphenPipe.transform(itInfo.accountInfoBanner.subAccountNumber),
                incomePercent: parseFloat(transactionDetails.trustIncomePercent!),
                principalPercent: parseFloat(transactionDetails.trustPrincipalPercent!),
                paidForContact: selectedContact,
                settlementDate: this.datepipe.transform(transactionDetails.processDate, 'yyyy-MM-dd') ?? '',
                tradeDate: new Date().toISOString(),
                taxCode: transactionDetails.trustTaxCode!,
                comment: transactionDetails.trustComment!,
                transactionCode: transactionCodes.internalTransferFromPtrs
            };
        }

        if (itInfo.toAccountInfo.isTrust && !itInfo.isTrustNonPTRSAccount) {
            const toAccount: string = this.hyphenPipe.transform(itInfo.toAccountInfo.accountNumber);
            const defaultContact: PaidForDetails = {
                id: -1,
                name: 'Paid For The Trust',
                role: 'Owner'
            };
            toTrustFields = {
                accountNumber: toAccount,
                incomePercent: parseFloat(transactionDetails.trustIncomeToPercent!),
                principalPercent: parseFloat(transactionDetails.trustPrincipalToPercent!),
                paidForContact: defaultContact,
                settlementDate: this.datepipe.transform(transactionDetails.processDate, 'yyyy-MM-dd') ?? '',
                tradeDate: new Date().toISOString(),
                taxCode: transactionDetails.trustDepositCode!,
                comment: transactionDetails.trustComment!,
                transactionCode: transactionCodes.internalTransferToPtrs
            };
        }

        return { fromTrustFields, toTrustFields };
    }

    getTransactionType(viewType: string, payload: any) {
        if ((payload['txnCode'] && payload['txnCode'].charAt(1) === 'T') || viewType === ViewType.standingAuth) {
            return RequestTransactionType.JournalSloa;
        }
        else if ((payload['txnCode'] && payload['txnCode'].charAt(1) === 'E')) {
            return RequestTransactionType.JournalOneTime;
        }
        else {
            return RequestTransactionType.JournalRecurring;
        }
    }

    isSloaSelected(extraPrompts: ItExtraPrompts): boolean {
        const isBundledtransaction = extraPrompts.useStandingAuth === 'bundled';
        return isBundledtransaction ? extraPrompts.useBundledStandingAuth === 'yes' : extraPrompts.useStandingAuth === 'yes';
    }
}

export function mapCustomerListSloa(isCreateSloa: boolean, selectedEauthCustomer: EAuthModelCustomer[], eauthModel: EAuthModel | undefined): EAuthModelCustomer[] {
    let approvers: EAuthModelCustomer[] = [];
    if (isCreateSloa) {
        approvers = selectedEauthCustomer;
    }
    return approvers;
}
